// ─────────────────────────────────────────────────────────────────────────────
// Client HTTP générique (fetch) — aucune connaissance des URLs ni des endpoints.
// Les instances configurées sont dans ./client.ts.
// ─────────────────────────────────────────────────────────────────────────────

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

export interface RequestOptions {
  /** Paramètres de query string — null/undefined sont ignorés, encodage automatique. */
  params?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * Délai max (ms) avant abandon. Un dépassement est traité comme une erreur
   * réseau (ApiError status 0, isTimeout). Sans valeur : pas de limite.
   */
  timeoutMs?: number;
  /**
   * Envoie le token Bearer et déclenche le handler 401 (défaut : true).
   * Mettre à false pour les appels publics (login, OTP, config).
   */
  auth?: boolean;
}

export interface ApiResponse<T> {
  data: T;
  status: number;
}

export class ApiError extends Error {
  constructor(
    message: string,
    /** Code HTTP, ou 0 si le serveur n'a pas pu être joint. */
    readonly status: number,
    readonly url: string,
    /** Message renvoyé par le serveur (champ message/error/detail/title), s'il existe. */
    readonly serverMessage?: string,
    /** Corps de réponse brut (JSON parsé ou texte). */
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isNetworkError(): boolean {
    return this.status === 0;
  }

  /** Le serveur n'a pas répondu dans le délai (timeoutMs). */
  get isTimeout(): boolean {
    return this.status === 0 && this.message.startsWith("Délai dépassé");
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

/** Message lisible pour l'utilisateur, quel que soit le type d'erreur. */
export function getErrorMessage(
  err: unknown,
  fallback = "Une erreur est survenue.",
): string {
  if (err instanceof ApiError) {
    if (err.isNetworkError) return "Serveur inaccessible. Vérifiez votre connexion.";
    return err.serverMessage || fallback;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function buildQuery(params?: QueryParams): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    const values = Array.isArray(value) ? value : [value];
    for (const v of values) {
      if (v !== undefined && v !== null) search.append(key, String(v));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204) return undefined;
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractServerMessage(body: unknown): string | undefined {
  if (body && typeof body === "object") {
    const b = body as Record<string, unknown>;
    for (const key of ["message", "error", "detail", "title"]) {
      const v = b[key];
      if (typeof v === "string" && v.trim()) return v;
    }
    return undefined;
  }
  // Texte brut (hors page HTML d'erreur)
  if (typeof body === "string" && body.trim() && !body.trimStart().startsWith("<")) {
    return body;
  }
  return undefined;
}

export interface HttpClientConfig {
  /** URL de base, ou résolveur (ex: URL lue depuis l'API au runtime). */
  baseUrl: string | (() => string | Promise<string>);
  getToken?: () => string | null;
  onUnauthorized?: () => void;
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export function createHttpClient(config: HttpClientConfig) {
  const resolveBaseUrl = async (): Promise<string> => {
    const base =
      typeof config.baseUrl === "function" ? await config.baseUrl() : config.baseUrl;
    return base.replace(/\/+$/, "");
  };

  async function request<T>(
    method: Method,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<ApiResponse<T>> {
    const { params, headers, signal, auth = true, timeoutMs } = options;
    const url = `${await resolveBaseUrl()}${path}${buildQuery(params)}`;
    const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

    const finalHeaders: Record<string, string> = {
      Accept: "application/json",
      ...headers,
    };
    // FormData : le navigateur pose lui-même le Content-Type (boundary multipart)
    if (body !== undefined && !isFormData) finalHeaders["Content-Type"] = "application/json";
    const token = auth ? config.getToken?.() : null;
    if (token) finalHeaders.Authorization = `Bearer ${token}`;

    // Annulation : signal de l'appelant + délai éventuel (couvre aussi la lecture du corps)
    const controller = new AbortController();
    let timedOut = false;
    const onCallerAbort = () => controller.abort();
    if (signal) {
      if (signal.aborted) controller.abort();
      else signal.addEventListener("abort", onCallerAbort, { once: true });
    }
    const timer = timeoutMs
      ? setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, timeoutMs)
      : undefined;

    let res: Response;
    let data: unknown;
    try {
      res = await fetch(url, {
        method,
        headers: finalHeaders,
        signal: controller.signal,
        body:
          body === undefined
            ? undefined
            : isFormData
              ? (body as FormData)
              : JSON.stringify(body),
      });
      data = await parseBody(res);
    } catch (err) {
      if (timedOut) {
        throw new ApiError(`Délai dépassé (${timeoutMs} ms) : ${method} ${url}`, 0, url, undefined, err);
      }
      if ((err as Error)?.name === "AbortError") throw err;
      throw new ApiError(`Serveur inaccessible (${method} ${url})`, 0, url, undefined, err);
    } finally {
      if (timer) clearTimeout(timer);
      signal?.removeEventListener("abort", onCallerAbort);
    }

    if (!res.ok) {
      if (res.status === 401 && auth) config.onUnauthorized?.();
      const serverMessage = extractServerMessage(data);
      throw new ApiError(
        serverMessage ?? `${method} ${path} → HTTP ${res.status}`,
        res.status,
        url,
        serverMessage,
        data,
      );
    }

    return { data: data as T, status: res.status };
  }

  return {
    get: <T>(path: string, options?: RequestOptions) =>
      request<T>("GET", path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>("POST", path, body, options),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>("PUT", path, body, options),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
      request<T>("PATCH", path, body, options),
    delete: <T = void>(path: string, options?: RequestOptions) =>
      request<T>("DELETE", path, undefined, options),
  };
}

export type HttpClient = ReturnType<typeof createHttpClient>;
